import { fetchJSON } from "Helper/apiHelper";

import CookieService from "./CookieService";

enum LoginSate {
  LOGGED_IN = "LOGGED_IN",
  LOGGED_OUT = "LOGGED_OUT",
}

type AuthenticatedUser = {
  UID: string;
  UIDSignature: string;
  signatureTimestamp: string;
  email: string;
};

// {@link https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/417de3e270b21014bbc5a10ce4041860.html}
export interface UserObject {
  firstName: string;
  lastName: string;
  // 0 when the user is not logged in.
  // 200 when user is authenticated
  // but not clearly documented as such
  statusCode: number;
  // empty string when the user is not authenticated
  UID: string;
  UIDSignature: string;
  signatureTimestamp: string;
  email: string;
}
// {@link https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/417623f770b21014bbc5a10ce4041860.html}
interface UserInfo {
  user: UserObject;
  // not being `0` means something went wrong (e.g. network issue) not if the user is logged in or not
  errorCode: number;
  // "This field will appear in the response only in case of an error and will contain the exception info, if available."
  errorDetails?: string;
  // "A short textual description of an error, associated with the errorCode, for logging purposes. This field will appear in the response only in case of an error."
  errorMessage?: string;
  // available when the user has been logged in
  UID?: string;
  UIDSignature?: string;
  signatureTimestamp?: string;
  email?: string;
}

// {@link https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/41532ab870b21014bbc5a10ce4041860.html}
interface LoginEventData {
  eventName: "login";
  fullEventName: string;
  isGlobal: boolean;
  isSiteUID: boolean;
  loginMode: string;
  newUser: boolean;
  provider: string;
  source: string;
  UID: string;
  UIDSignature: string;
  signatureTimestamp: string;
  email: string;
  user: UserObject;
}

// @see {https://developers.gigya.com/display/GD/accounts.addEventHandlers+JS}
type GigyaResponse = {
  errorMessage: string;
  callId: string;
  context?: Record<string, unknown>;
  status: string;
  statusMessage: string;
} & (
  | ({
      errorCode: 0;
    } & AuthenticatedUser)
  | {
      errorCode: Exclude<number, 0>;
    }
);

type LogoutEventData = {
  eventName: string;
  context?: Record<string, unknown>;
};

export default class SSOService {
  static _instance: SSOService;

  // cookie which will be set with user info. BE will use it to make sure user is authenticated
  static get COOKIE_NAME() {
    return "rhp_231c1d3c76u3s2er980i4163n876fo";
  }

  // local storage entry to signal to other tabs that the user auth status has changed
  static get LOCAL_STORAGE_NAME() {
    return "FRA_SSO_STATUS";
  }

  static getInstance(): SSOService {
    if (!SSOService._instance) {
      SSOService._instance = new SSOService();
    }
    return SSOService._instance;
  }

  // user is currently logged-in
  // undefined means on page load the current auth hasn't been determined yet
  _loggedIn?: boolean = undefined;

  // a promise which is resolved when the gigya SDK is loaded
  _whenReadyPromise: Promise<void> | null = null;

  // callbacks which will be called when the auth status changes
  subscribers: Function[] = [];

  /**
   * user is currently logged-in.
   * Will be false when the login state hasn't been determined yet
   *
   * @readonly
   * @type {boolean}
   * @memberof SSOService
   */
  get loggedIn(): boolean {
    return Boolean(this._loggedIn);
  }

  constructor() {
    this.whenGigyaReady().then(async () => {
      const userInfo = await this.getUserInfo();
      this.onLoginChange(userInfo.user);

      // {@link https://help.sap.com/docs/SAP_CUSTOMER_DATA_CLOUD/8b8d6fffe113457094a17701f63e3d6a/41739dca70b21014bbc5a10ce4041860.html}
      window.gigya.socialize.addEventHandlers({
        // only called on the first page load after the login page
        onLogin: (e: LoginEventData) => {
          this.onLoginChange(e.user);
        },
        // called when the user logs out e.g. via logout-button
        onLogout: (_e: LogoutEventData) => {
          this.onLoginChange(null);
        },
      });

      // auth status has changed in other tabs. get latest info from gigya
      window.addEventListener("storage", async e => {
        if (e.key === SSOService.LOCAL_STORAGE_NAME) {
          const user = (await this.getUserInfo()).user;
          this.onLoginChange(user);
        }
      });
    });
  }

  // returns a promise which will be resolved when the gigya script is loaded any apis ready to be used
  async whenGigyaReady() {
    if (this._whenReadyPromise) return this._whenReadyPromise;

    this._whenReadyPromise =
      // script is already loaded
      "gigya" in window
        ? Promise.resolve()
        : // wait for js callback
          new Promise<void>(resolve => {
            window.onGigyaServiceReady = function onGigyaServiceReady() {
              resolve();
            };
          });

    return this._whenReadyPromise;
  }

  // returns "AccountInfo" according to gigya
  // @ts-ignore - not currently used. but might be re-used again
  private async getAccountInfo() {
    return new Promise<GigyaResponse>((resolve, _reject) => {
      // {@link https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/04d7b87fe17c4091839cae8c6a887efd.html}
      window.gigya.accounts.getAccountInfo({
        // include: "identities-active, identities-all, identities-global, loginIDs, emails,  profile, data, regSource, irank, isLockedOut, lastLoginLocation, rba, subscriptions, userInfo, preferences, groups, id_token",
        callback: (response: GigyaResponse) => {
          resolve(response);
        },
      });
    });
  }

  async getUserInfo(): Promise<UserInfo> {
    await this.whenGigyaReady();
    return new Promise<UserInfo>((resolve, reject) => {
      // {@link https://help.sap.com/viewer/8b8d6fffe113457094a17701f63e3d6a/GIGYA/en-US/417623f770b21014bbc5a10ce4041860.html}
      window.gigya.socialize.getUserInfo({
        callback(userInfo: UserInfo) {
          if (userInfo.errorCode !== 0) {
            reject(userInfo.errorMessage!);
            return;
          }
          resolve(userInfo);
        },
      });
    });
  }

  async onLoginChange(user: UserObject | null) {
    const loggedIn = Boolean(user?.UID);
    // no change doesn't need to react
    if (loggedIn === this._loggedIn) return;

    this._loggedIn = loggedIn;

    // store cookie which will be used by BE in various requests.
    if (loggedIn) {
      this._setCookie({
        UID: user!.UID,
        UIDSignature: user!.UIDSignature,
        signatureTimestamp: user!.signatureTimestamp,
        email: user!.email,
      });
      // let other tabs know login state has changed (it also might already have the correct login state)
      window.localStorage.setItem(SSOService.LOCAL_STORAGE_NAME, LoginSate.LOGGED_IN);
    } else {
      this._removeCookie();
      window.localStorage.setItem(SSOService.LOCAL_STORAGE_NAME, LoginSate.LOGGED_OUT);
    }

    // this is actually needed only after `onLogin`, `onLogout` gigya callbacks when the user really changed their login state,
    // and at midnight when the BE's stored login date is not the current date anymore
    // so we are calling it each time just to be sure
    await this._callServletToUpdateCookie();

    // call subscribers
    this.subscribers.forEach(cb => {
      cb({
        loggedIn: this.loggedIn,
        user,
      });
    });
  }

  // store user auth info to the cookie for BE to use it
  private _setCookie({
    UID,
    UIDSignature,
    signatureTimestamp,
    email,
  }: {
    UID: UserObject["UID"];
    UIDSignature: UserObject["UIDSignature"];
    signatureTimestamp: UserObject["signatureTimestamp"];
    email: UserObject["email"];
  }) {
    CookieService.setCookie(
      SSOService.COOKIE_NAME,
      window.btoa(`${UID}||${UIDSignature}||${signatureTimestamp}||${email}`),
      null,
    );
  }

  private _removeCookie() {
    CookieService.removeCookie(SSOService.COOKIE_NAME);
  }

  // server needs to validate and store some information regarding the user login state to re-use it in other places such as park booking
  // the servlet can only be called in the first 60 seconds after the gigya `getAccountInfo` or `getUserInfo` was called
  private async _callServletToUpdateCookie() {
    // remove any query params and hash. and replace the .html with the resource path for the servlet
    const baseUrl = location.origin + location.pathname.replace(/.html$/, "/_jcr_content.");
    const servletUrl = `${baseUrl}${this._loggedIn ? "login.json" : "logout.json"}`;

    try {
      await fetchJSON(servletUrl);
    } catch (error) {
      // should only throw in FE Click Dummy,
      // in AEM only fails when server is down e.g.
      console.error(error);
    }
  }

  /**
   * provided callback will be called when the login status changes.
   * this includes the auth status on load
   *
   * @param {Function} cb
   * @memberof SSOService
   */
  subscribe(cb: ({ loggedIn, user }: { loggedIn: boolean; user: UserObject | null }) => unknown) {
    if (!this.subscribers.includes(cb)) this.subscribers.push(cb);
  }

  /**
   * unsubscribe callback subscribed in {SSOService.subscribe}
   *
   * @param {Function} cb
   * @memberof SSOService
   */
  unsubscribe(cb: Function) {
    const index = this.subscribers.indexOf(cb);
    if (index !== -1) {
      this.subscribers.splice(index, 1);
    }
  }

  /**
   * load gigyas screenSet
   *
   * @param {Object} options
   * @memberof SSOService
   */
  async showScreenSet({
    screenSet,
    containerID,
    startScreen,
    screenSetLang,
  }: {
    screenSet: string;
    containerID: string;
    startScreen: string;
    screenSetLang: string;
  }) {
    await this.whenGigyaReady();
    window.gigya.accounts.showScreenSet({
      screenSet,
      lang: screenSetLang || "en",
      containerID,
      startScreen,
    });
  }

  /**
   * user is currently logged-in.
   * Will be false when the login state hasn't been determined yet
   *
   * @readonly
   * @type {string}
   * @param {string} proxyPageUrl to enrich
   * @return {string} the enriched redirect url
   * @memberof SSOService
   */
  getProxyPageRedirectUrl(proxyPageUrl: string): string {
    return window.gigya.utils.URL.addParamsToURL(proxyPageUrl, {
      mode: "afterLogin",
    });
  }

  /**
   * logouts gigyas user
   *
   * @return {Promise<void>}
   */
  logOut() {
    return new Promise<void>((resolve, reject) => {
      window.gigya.accounts.logout({
        callback: ({ errorCode, errorMessage }: { errorCode: number; errorMessage: string }) => {
          if (errorCode === 0) resolve();
          else {
            console.error(errorMessage);
            reject();
          }
        },
      });
    });
  }

  /**
   * logouts gigyas user
   * @param {any} cb
   * @param {any} arg
   *
   * @memberof SSOService
   */
  logOutCallback(cb: any, arg: any) {
    window.gigya.accounts.logout({ callback: cb(arg) });
  }
}
